import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { API } from '../utils/config';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  Building,
  User,
  CheckCircle,
  AlertCircle,
  Home,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Copy,
  RefreshCw
} from 'lucide-react';


const PartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPartner, setShowNewPartner] = useState(false);
  const [showEditPartner, setShowEditPartner] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('all');
  const [completenessFilter, setCompletenessFilter] = useState('all'); // NEW: completeness filter
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Duplicates state
  const [duplicatesInfo, setDuplicatesInfo] = useState(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [removingDuplicates, setRemovingDuplicates] = useState(false);
  
  const [newPartner, setNewPartner] = useState({
    name: '',
    entity_type: 'private',
    phone: '',
    email: '',
    address: '',
    city: '',
    province: '',
    country: 'Italia',
    partner_code: '',
    sponsor: '',
    is_also_manager: false,
    notes: ''
  });

  useEffect(() => {
    fetchPartners();
    fetchProperties();
  }, []);

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching partners:', error);
      showMessage('error', 'Errore nel caricamento dei partner');
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Check for duplicates
  const checkDuplicates = async () => {
    setCheckingDuplicates(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/admin/check-duplicates/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuplicatesInfo(response.data);
      if (response.data.duplicates_count === 0) {
        showMessage('success', 'Nessun duplicato trovato!');
      } else {
        showMessage('warning', `Trovati ${response.data.duplicates_count} gruppi di duplicati`);
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
      showMessage('error', 'Errore nel controllo duplicati');
    }
    setCheckingDuplicates(false);
  };

  // Remove duplicates (keep most complete)
  const removeDuplicates = async () => {
    if (!window.confirm('Sei sicuro di voler rimuovere i duplicati? Verrà mantenuto il record più completo per ogni gruppo.')) {
      return;
    }
    setRemovingDuplicates(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(`${API}/api/admin/remove-duplicates/partners`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', `Rimossi ${response.data.removed_count} duplicati. Totale partner: ${response.data.total_remaining}`);
      setDuplicatesInfo(null);
      fetchPartners(); // Ricarica la lista
    } catch (error) {
      console.error('Error removing duplicates:', error);
      showMessage('error', 'Errore nella rimozione duplicati');
    }
    setRemovingDuplicates(false);
  };

  const handleCreatePartner = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/api/partners`, newPartner, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showMessage('success', 'Partner creato con successo');
      setShowNewPartner(false);
      resetForm();
      fetchPartners();
    } catch (error) {
      console.error('Error creating partner:', error);
      showMessage('error', error.response?.data?.detail || 'Errore nella creazione del partner');
    }
  };

  const handleUpdatePartner = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hw_token');
      await axios.put(`${API}/api/partners/${selectedPartner.id}`, newPartner, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showMessage('success', 'Partner aggiornato con successo');
      setShowEditPartner(false);
      setSelectedPartner(null);
      resetForm();
      fetchPartners();
    } catch (error) {
      console.error('Error updating partner:', error);
      showMessage('error', error.response?.data?.detail || 'Errore nell\'aggiornamento del partner');
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo partner?')) {
      return;
    }

    try {
      const token = localStorage.getItem('hw_token');
      await axios.delete(`${API}/api/partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showMessage('success', 'Partner eliminato con successo');
      fetchPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      showMessage('error', error.response?.data?.detail || 'Errore nell\'eliminazione del partner');
    }
  };

  const openEditDialog = (partner) => {
    setSelectedPartner(partner);
    setNewPartner({
      name: partner.name,
      entity_type: partner.entity_type,
      phone: partner.phone,
      email: partner.email,
      address: partner.address,
      city: partner.city,
      province: partner.province,
      country: partner.country,
      partner_code: partner.partner_code,
      sponsor: partner.sponsor || '',
      is_also_manager: partner.is_also_manager,
      notes: partner.notes || ''
    });
    setShowEditPartner(true);
  };

  const resetForm = () => {
    setNewPartner({
      name: '',
      entity_type: 'private',
      phone: '',
      email: '',
      address: '',
      city: '',
      province: '',
      country: 'Italia',
      partner_code: '',
      sponsor: '',
      is_also_manager: false,
      notes: ''
    });
  };

  const getPartnerProperties = (partnerCode) => {
    return properties.filter(p => p.partner_code === partnerCode);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 text-slate-400" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  const filteredPartners = partners
    .filter(partner => {
      const matchesSearch = partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           partner.partner_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           partner.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = entityTypeFilter === 'all' || partner.entity_type === entityTypeFilter;
      const matchesCity = !cityFilter || (partner.city && partner.city.toLowerCase().includes(cityFilter.toLowerCase()));
      const matchesManager = managerFilter === 'all' || 
                            (managerFilter === 'yes' && partner.is_also_manager) ||
                            (managerFilter === 'no' && !partner.is_also_manager);
      const matchesCompleteness = completenessFilter === 'all' || 
                                 (completenessFilter === 'complete' && !partner.is_incomplete) ||
                                 (completenessFilter === 'incomplete' && partner.is_incomplete);
      return matchesSearch && matchesType && matchesCity && matchesManager && matchesCompleteness;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch(sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'code':
          aValue = a.partner_code;
          bValue = b.partner_code;
          break;
        case 'city':
          aValue = a.city || '';
          bValue = b.city || '';
          break;
        case 'properties':
          aValue = getPartnerProperties(a.partner_code).length;
          bValue = getPartnerProperties(b.partner_code).length;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  if (loading) {
    return (
      <Layout title="Partner">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
            <p className="text-slate-600">Caricamento partner...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Partner">
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="h-8 w-8 text-blue-900" />
              Gestione Partner
              <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-800 text-lg font-semibold rounded-full">
                {partners.length}
              </span>
            </h1>
            <p className="text-slate-600 mt-2">
              Partner che acquisiscono immobili per HolidayWorld
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={checkDuplicates}
              disabled={checkingDuplicates}
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              {checkingDuplicates ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              Controlla Duplicati
            </Button>
            <Button
              onClick={() => setShowNewPartner(true)}
              className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Partner
            </Button>
          </div>
        </div>

        {/* Duplicates Alert */}
        {duplicatesInfo && duplicatesInfo.duplicates_count > 0 && (
          <Card className="p-4 bg-orange-50 border-orange-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-orange-600" />
                <div>
                  <p className="font-semibold text-orange-800">
                    Trovati {duplicatesInfo.duplicates_count} gruppi di duplicati
                  </p>
                  <p className="text-sm text-orange-600">
                    Verranno mantenuti i record più completi
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setDuplicatesInfo(null)}
                  variant="outline"
                  size="sm"
                >
                  Chiudi
                </Button>
                <Button
                  onClick={removeDuplicates}
                  disabled={removingDuplicates}
                  className="bg-orange-600 hover:bg-orange-700"
                  size="sm"
                >
                  {removingDuplicates ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Rimuovi Duplicati
                </Button>
              </div>
            </div>
            {/* Mostra dettagli duplicati */}
            <div className="mt-4 max-h-40 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-orange-800">
                  <tr>
                    <th className="text-left py-1">Codice</th>
                    <th className="text-left py-1">Occorrenze</th>
                    <th className="text-left py-1">Da mantenere</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicatesInfo.duplicates.slice(0, 10).map((dup, idx) => (
                    <tr key={idx} className="border-t border-orange-200">
                      <td className="py-1 font-mono">{dup.key}</td>
                      <td className="py-1">{dup.count}x</td>
                      <td className="py-1">{dup.keep?.doc?.name || '-'}</td>
                    </tr>
                  ))}
                  {duplicatesInfo.duplicates.length > 10 && (
                    <tr className="text-orange-600">
                      <td colSpan="3" className="py-1">...e altri {duplicatesInfo.duplicates.length - 10}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Message Alert */}
        {message.text && (
          <div className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            {message.text}
          </div>
        )}

        {/* Filters */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="Cerca per nome, codice o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div>
              <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo entità" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  <SelectItem value="private">Privati</SelectItem>
                  <SelectItem value="company">Aziende</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="Filtra per città..."
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="pl-10"
              />
            </div>
            <div>
              <Select value={managerFilter} onValueChange={setManagerFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Anche responsabile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="yes">Anche responsabili</SelectItem>
                  <SelectItem value="no">Solo partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={completenessFilter} onValueChange={setCompletenessFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Stato dati" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="complete">✅ Completi</SelectItem>
                  <SelectItem value="incomplete">⚠️ Incompleti</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Partners Table */}
        <Card className="overflow-hidden shadow-lg">
          <div className="overflow-x-auto max-h-[calc(100vh-300px)] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-100 to-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  <th 
                    className="px-6 py-4 text-left text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Partner
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Tipo
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                    onClick={() => handleSort('code')}
                  >
                    <div className="flex items-center gap-2">
                      Codice
                      {getSortIcon('code')}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Contatti
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                    onClick={() => handleSort('city')}
                  >
                    <div className="flex items-center gap-2">
                      Zona
                      {getSortIcon('city')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                    onClick={() => handleSort('properties')}
                  >
                    <div className="flex items-center gap-2">
                      Immobili
                      {getSortIcon('properties')}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Anche Resp.
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredPartners.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-slate-500">
                      <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-lg font-medium">Nessun partner trovato</p>
                      <p className="text-sm mt-2">Aggiungi il tuo primo partner</p>
                    </td>
                  </tr>
                ) : (
                  filteredPartners.map((partner) => {
                    const partnerProps = getPartnerProperties(partner.partner_code);
                    const isIncomplete = partner.is_incomplete || false;
                    return (
                      <tr 
                        key={partner.id} 
                        className={`hover:bg-slate-50 transition-colors ${
                          isIncomplete ? 'border-l-4 border-l-red-500 bg-red-50/30' : ''
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {partner.entity_type === 'company' ? (
                              <Building className="h-5 w-5 text-blue-600" />
                            ) : (
                              <User className="h-5 w-5 text-green-600" />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-900">{partner.name}</p>
                                {isIncomplete && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                                    <AlertCircle className="h-3 w-3" />
                                    Dati Mancanti
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">{partner.email}</p>
                              {isIncomplete && partner.missing_fields && partner.missing_fields.length > 0 && (
                                <p className="text-xs text-red-600 mt-1">
                                  Mancanti: {partner.missing_fields.slice(0, 3).join(', ')}
                                  {partner.missing_fields.length > 3 && ` +${partner.missing_fields.length - 3}`}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            partner.entity_type === 'company'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {partner.entity_type === 'company' ? 'Azienda' : 'Privato'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">
                            {partner.partner_code}
                          </code>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">
                          <p>{partner.phone}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">
                          <p>{partner.city}, {partner.province}</p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Home className="h-4 w-4 text-blue-600" />
                            <span className="font-semibold text-blue-900">{partnerProps.length}</span>
                            {partnerProps.length > 0 && (
                              <span className="text-xs text-slate-500">immobili</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {partner.is_also_manager && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                              <CheckCircle className="h-3 w-3" />
                              Sì
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(partner)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeletePartner(partner.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Create/Edit Partner Dialog */}
        {(showNewPartner || showEditPartner) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                {showEditPartner ? 'Modifica Partner' : 'Nuovo Partner'}
              </h2>
              <form onSubmit={showEditPartner ? handleUpdatePartner : handleCreatePartner} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nome / Ragione Sociale *</Label>
                    <Input
                      type="text"
                      value={newPartner.name}
                      onChange={(e) => setNewPartner({...newPartner, name: e.target.value})}
                      placeholder="Mario Rossi / Rossi Immobiliare SRL"
                      required
                    />
                  </div>
                  <div>
                    <Label>Tipo *</Label>
                    <Select 
                      value={newPartner.entity_type}
                      onValueChange={(value) => setNewPartner({...newPartner, entity_type: value})}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private">Privato</SelectItem>
                        <SelectItem value="company">Azienda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Telefono *</Label>
                    <Input
                      type="tel"
                      value={newPartner.phone}
                      onChange={(e) => setNewPartner({...newPartner, phone: e.target.value})}
                      placeholder="+39 123 456 7890"
                      required
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newPartner.email}
                      onChange={(e) => setNewPartner({...newPartner, email: e.target.value})}
                      placeholder="partner@example.com"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Indirizzo *</Label>
                    <Input
                      type="text"
                      value={newPartner.address}
                      onChange={(e) => setNewPartner({...newPartner, address: e.target.value})}
                      placeholder="Via Roma, 123"
                      required
                    />
                  </div>
                  <div>
                    <Label>Città *</Label>
                    <Input
                      type="text"
                      value={newPartner.city}
                      onChange={(e) => setNewPartner({...newPartner, city: e.target.value})}
                      placeholder="Roma"
                      required
                    />
                  </div>
                  <div>
                    <Label>Provincia *</Label>
                    <Input
                      type="text"
                      value={newPartner.province}
                      onChange={(e) => setNewPartner({...newPartner, province: e.target.value})}
                      placeholder="RM"
                      required
                    />
                  </div>
                  <div>
                    <Label>Paese *</Label>
                    <Input
                      type="text"
                      value={newPartner.country}
                      onChange={(e) => setNewPartner({...newPartner, country: e.target.value})}
                      placeholder="Italia"
                      required
                    />
                  </div>
                  <div>
                    <Label>Codice Partner *</Label>
                    <Input
                      type="text"
                      value={newPartner.partner_code}
                      onChange={(e) => setNewPartner({...newPartner, partner_code: e.target.value.toUpperCase()})}
                      placeholder="PTR-001"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Codice univoco per associare immobili
                    </p>
                  </div>
                  <div>
                    <Label>Sponsor</Label>
                    <Input
                      type="text"
                      value={newPartner.sponsor}
                      onChange={(e) => setNewPartner({...newPartner, sponsor: e.target.value})}
                      placeholder="Chi ha portato il partner in HW"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Note</Label>
                    <textarea
                      value={newPartner.notes}
                      onChange={(e) => setNewPartner({...newPartner, notes: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                      placeholder="Note aggiuntive..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_also_manager"
                        checked={newPartner.is_also_manager}
                        onChange={(e) => setNewPartner({...newPartner, is_also_manager: e.target.checked})}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="is_also_manager" className="text-sm font-medium text-slate-700">
                        ✅ È anche Responsabile Immobili
                      </label>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-6">
                      Se selezionato, verrà creato automaticamente anche come Responsabile
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-4 border-t">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setShowNewPartner(false);
                      setShowEditPartner(false);
                      setSelectedPartner(null);
                      resetForm();
                    }}
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700"
                  >
                    {showEditPartner ? 'Aggiorna' : 'Crea Partner'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PartnersManager;
